import { type GraphQLResult, getApiClient } from './apiClient';
import { authService } from './authService';

export interface KeyboxItem {
  id: string;
  label: string | null;
  ecFingerprint: string | null;
  rsaFingerprint: string | null;
  status: string;
  uploadedBy: string | null;
  uploadedAt: number | null;
  activeEc: boolean;
  activeRsa: boolean;
}

export interface KeyboxExportResult {
  result: boolean;
  message?: string;
  /** Nội dung keybox.xml dựng lại. Chỉ có khi result = true. */
  xml?: string;
  filename?: string;
  status?: string;
  /** Số lần export còn lại trong cửa sổ 1 giờ. */
  remainingToday?: number;
}

export interface KeyboxMutationResult {
  result: boolean;
  message?: string;
  id?: string;
  ecFingerprint?: string;
  rsaFingerprint?: string;
}

// apiClient trích op từ field đầu + POST variables tới /api/v1/<op>, bọc lại {data:{op:...}}.
const listQuery = /* GraphQL */ `query ListKeyboxes { listKeyboxes { keyboxes } }`;
const uploadMutation = /* GraphQL */ `mutation UploadKeybox($keyboxXml: String!, $label: String) {
  uploadKeybox(keyboxXml: $keyboxXml, label: $label) { result message id ecFingerprint rsaFingerprint } }`;
const setActiveMutation = /* GraphQL */ `mutation SetActiveKeybox($id: String!, $slot: String) {
  setActiveKeybox(id: $id, slot: $slot) { result message } }`;
const revokeMutation = /* GraphQL */ `mutation RevokeKeybox($id: String!) {
  revokeKeybox(id: $id) { result message } }`;
const exportMutation = /* GraphQL */ `mutation ExportKeybox($id: String!, $password: String!) {
  exportKeybox(id: $id, password: $password) { result message xml filename status remainingToday } }`;

const checkAdmin = () => {
  if (!authService.isAuthenticated()) throw new Error('No authenticated user');
  if (!authService.getGroups().includes('Admin')) throw new Error('Admin only');
};

const errOf = (r: GraphQLResult<unknown>) => {
  if (r.errors) throw new Error(r.errors.map((e) => e.message).join(', '));
};

export const keyboxService = {
  async list(): Promise<KeyboxItem[]> {
    checkAdmin();
    const r = (await getApiClient().graphql({ query: listQuery, authMode: 'userPool' })) as GraphQLResult<{
      listKeyboxes: { keyboxes: KeyboxItem[] };
    }>;
    errOf(r);
    return r.data?.listKeyboxes?.keyboxes ?? [];
  },

  async upload(keyboxXml: string, label: string): Promise<KeyboxMutationResult> {
    checkAdmin();
    const r = (await getApiClient().graphql({
      query: uploadMutation,
      variables: { keyboxXml, label },
      authMode: 'userPool',
    })) as GraphQLResult<{ uploadKeybox: KeyboxMutationResult }>;
    errOf(r);
    return r.data!.uploadKeybox;
  },

  async setActive(id: string, slot: 'EC' | 'RSA' | 'both'): Promise<KeyboxMutationResult> {
    checkAdmin();
    const r = (await getApiClient().graphql({
      query: setActiveMutation,
      variables: { id, slot },
      authMode: 'userPool',
    })) as GraphQLResult<{ setActiveKeybox: KeyboxMutationResult }>;
    errOf(r);
    return r.data!.setActiveKeybox;
  },

  /**
   * Dựng lại keybox.xml. Server bắt nhập lại mật khẩu, ghi audit và giới hạn
   * 5 lần/giờ — đây là đường DUY NHẤT private key rời khỏi server.
   */
  async exportXml(id: string, password: string): Promise<KeyboxExportResult> {
    checkAdmin();
    const r = (await getApiClient().graphql({
      query: exportMutation,
      variables: { id, password },
      authMode: 'userPool',
    })) as GraphQLResult<{ exportKeybox: KeyboxExportResult }>;
    errOf(r);
    return r.data!.exportKeybox;
  },

  async revoke(id: string): Promise<KeyboxMutationResult> {
    checkAdmin();
    const r = (await getApiClient().graphql({
      query: revokeMutation,
      variables: { id },
      authMode: 'userPool',
    })) as GraphQLResult<{ revokeKeybox: KeyboxMutationResult }>;
    errOf(r);
    return r.data!.revokeKeybox;
  },
};
